/**
 * AI-Powered Product Import Component
 * Features:
 * - Parse any Excel format to product format
 * - Extract product info from images
 * - Smart suggestions and variant generation
 * - Fallback when AI unavailable
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
  LinearProgress,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  AutoAwesome as AIIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Lightbulb as SuggestionIcon,
  Add as AddIcon,
  Inventory as StockIcon,
  RoomService as ServiceIcon
} from '@mui/icons-material';
import axios from 'axios';
import { HEADERS } from '../../../../../API/UrlAndPaths';

const steps = ['Upload File', 'Column Mapping', 'Review & Import'];

const ProductAIImport = ({ open, onClose, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0: Excel, 1: Image
  const [loading, setLoading] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Parsed data
  const [parsedProducts, setParsedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [parseInfo, setParseInfo] = useState(null);
  const [columnMapping, setColumnMapping] = useState(null);
  const [originalHeaders, setOriginalHeaders] = useState([]);
  
  // Import results
  const [importResults, setImportResults] = useState(null);
  
  // Categories and brands for autocomplete
  const [categories, setCategories] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]); // Full category objects with _id
  const [posCategories, setPosCategories] = useState([]);
  const [posCategoriesData, setPosCategoriesData] = useState([]); // Full POS category objects
  const [attributes, setAttributes] = useState([]);
  const [attributesData, setAttributesData] = useState([]); // Full attribute objects
  const [brands, setBrands] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [uomsData, setUomsData] = useState([]); // Full UOM objects
  
  // Default product type (Stock or Service)
  const [defaultProductType, setDefaultProductType] = useState('stock'); // 'stock' or 'service'

  // Check AI status on mount
  useEffect(() => {
    if (open) {
      checkAIStatus();
      loadMasterData();
    }
  }, [open]);

  const checkAIStatus = async () => {
    try {
      const response = await axios.get('api/product-ai/status', HEADERS);
      setAiStatus(response.data);
    } catch (err) {
      setAiStatus({ aiAvailable: false, fallbackAvailable: true });
    }
  };

  const loadMasterData = async () => {
    // Load categories - POST /category/view
    try {
      const catRes = await axios.post('category/view', {}, HEADERS);
      console.log('📦 Categories loaded:', catRes.data?.length || 0);
      if (catRes.data && Array.isArray(catRes.data)) {
        setCategoriesData(catRes.data);
        // Deduplicate category names to avoid React key warnings
        setCategories([...new Set(catRes.data.map(c => c.categoryName).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
    
    // Load POS categories - GET /inventory/viewposcategory
    try {
      const posCatRes = await axios.get('inventory/viewposcategory', HEADERS);
      console.log('📦 POS Categories loaded:', posCatRes.data?.length || 0);
      if (posCatRes.data && Array.isArray(posCatRes.data)) {
        setPosCategoriesData(posCatRes.data);
        // Deduplicate POS category names to avoid React key warnings
        setPosCategories([...new Set(posCatRes.data.map(c => c.name).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error loading POS categories:', err);
    }
    
    // Load attributes - GET /readymade/viewAttributes
    try {
      const attrRes = await axios.get('readymade/viewAttributes', HEADERS);
      console.log('📦 Attributes loaded:', attrRes.data?.length || 0);
      if (attrRes.data && Array.isArray(attrRes.data)) {
        setAttributesData(attrRes.data);
        // Deduplicate attribute names to avoid React key warnings
        setAttributes([...new Set(attrRes.data.map(a => a.attributeName).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error loading attributes:', err);
    }
    
    // Load brands - GET /readymade/getAllBrands
    try {
      const brandRes = await axios.get('readymade/getAllBrands', HEADERS);
      console.log('📦 Brands loaded:', brandRes.data?.length || 0);
      if (brandRes.data && Array.isArray(brandRes.data)) {
        // Deduplicate brand names to avoid React key warnings
        setBrands([...new Set(brandRes.data.map(b => b.brandName).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error loading brands:', err);
    }
    
    // Load UOMs - POST /purchase/listAllUoms
    try {
      const uomRes = await axios.post('purchase/listAllUoms', {}, HEADERS);
      console.log('📦 UOMs loaded:', uomRes.data?.length || 0, uomRes.data);
      if (uomRes.data && Array.isArray(uomRes.data)) {
        setUomsData(uomRes.data);
        // Deduplicate UOM names to avoid React key warnings
        setUoms([...new Set(uomRes.data.map(u => u.uomName).filter(Boolean))]);
      }
    } catch (err) {
      console.error('Error loading UOMs:', err);
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setParsedProducts([]);
    setParseInfo(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = type === 'excel' ? 'api/product-ai/parse-excel' : 'api/product-ai/parse-image';
      const response = await axios.post(endpoint, formData, {
        ...HEADERS,
        headers: {
          ...HEADERS.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const products = response.data.data.products || response.data.data?.products || [];
        
        // For image parsing, products are nested differently
        if (type === 'image' && response.data.data?.products) {
          setParsedProducts(response.data.data.products);
        } else {
          setParsedProducts(products);
        }
        
        setSelectedProducts(products.map((_, i) => i));
        setParseInfo({
          usedAI: response.data.data.usedAI || response.data.usedAI,
          totalRows: response.data.data.totalRows,
          parsedRows: response.data.data.parsedRows,
          confidence: response.data.data.confidence,
          notes: response.data.data.notes,
          detectedColumns: response.data.data.detectedColumns || response.data.data.columnMapping,
          sheetsProcessed: response.data.data.sheetsProcessed,
          totalSheets: response.data.data.totalSheets
        });
        
        // Store column mapping and headers for display
        setColumnMapping(response.data.data.detectedColumns || response.data.data.columnMapping);
        setOriginalHeaders(response.data.data.headers || []);
        
        setActiveStep(1);
        setSuccess(`Parsed ${products.length} products from ${response.data.data.totalRows || 'unknown'} rows!`);
      } else {
        setError(response.data.message || 'Failed to parse file');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...parsedProducts];
    updated[index] = { ...updated[index], [field]: value };
    setParsedProducts(updated);
  };

  // Get POS categories filtered by selected main category
  const getPosCategoriesForProduct = (product) => {
    if (!product.mainCategory) return posCategories;
    
    // Find the category ID for the selected main category
    const selectedCat = categoriesData.find(
      c => c.categoryName?.toLowerCase() === product.mainCategory?.toLowerCase()
    );
    
    if (!selectedCat) return posCategories;
    
    // Filter POS categories that belong to this main category
    const filtered = posCategoriesData
      .filter(pc => pc.catId === selectedCat._id || pc.catId?.toString() === selectedCat._id?.toString())
      .map(pc => pc.name);
    
    return filtered.length > 0 ? filtered : posCategories;
  };

  const handleSelectProduct = (index) => {
    if (selectedProducts.includes(index)) {
      setSelectedProducts(selectedProducts.filter(i => i !== index));
    } else {
      setSelectedProducts([...selectedProducts, index]);
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === parsedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(parsedProducts.map((_, i) => i));
    }
  };

  const handleDeleteProduct = (index) => {
    const updated = parsedProducts.filter((_, i) => i !== index);
    setParsedProducts(updated);
    setSelectedProducts(selectedProducts.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const handleImport = async () => {
    if (selectedProducts.length === 0) {
      setError('Please select at least one product to import');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const productsToImport = selectedProducts.map(i => parsedProducts[i]);
      
      const response = await axios.post('api/product-ai/bulk-import', {
        products: productsToImport,
        createMissing: true,
        defaultProductType: defaultProductType // 'stock' or 'service'
      }, HEADERS);

      if (response.data.success) {
        setImportResults(response.data.data);
        setActiveStep(2);
        setSuccess(response.data.message);
      } else {
        setError(response.data.message || 'Import failed');
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.message || err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get('api/product-ai/export-template', {
        ...HEADERS,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleGetSuggestions = async (category) => {
    if (!aiStatus?.aiAvailable) {
      setError('AI is not available for suggestions');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('api/product-ai/suggestions', {
        category,
        count: 5
      }, HEADERS);

      if (response.data.success && response.data.data) {
        const newProducts = response.data.data.map(p => ({
          name: p.name,
          nativeName: p.nativeName || '',
          mainCategory: category,
          posCategory: p.posCategory || '',
          salesPrice: p.suggestedPrice?.max || 0,
          cost: p.suggestedPrice?.min || 0
        }));
        
        setParsedProducts([...parsedProducts, ...newProducts]);
        setSelectedProducts([...selectedProducts, ...newProducts.map((_, i) => parsedProducts.length + i)]);
        setSuccess(`Added ${newProducts.length} AI suggestions!`);
      }
    } catch (err) {
      setError('Failed to get suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setParsedProducts([]);
    setSelectedProducts([]);
    setParseInfo(null);
    setImportResults(null);
    setError(null);
    setSuccess(null);
    onClose();
    if (importResults?.created > 0 || importResults?.updated > 0) {
      onImportComplete?.();
    }
  };

  const renderUploadStep = () => (
    <Box sx={{ p: 3 }}>
      {/* AI Status Banner */}
      <Alert 
        severity={aiStatus?.aiAvailable ? 'success' : 'info'} 
        sx={{ mb: 3 }}
        icon={<AIIcon />}
      >
        {aiStatus?.aiAvailable 
          ? '🤖 AI is enabled! Upload ANY Excel format - AI will auto-detect and map columns.'
          : '📋 Smart parsing enabled. Upload any Excel format - columns will be auto-detected.'}
      </Alert>

      {/* Stock/Service Selection */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 1 }}>
            Default Product Type for New Products
          </FormLabel>
          <RadioGroup
            row
            value={defaultProductType}
            onChange={(e) => setDefaultProductType(e.target.value)}
          >
            <FormControlLabel 
              value="stock" 
              control={<Radio />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StockIcon fontSize="small" color="primary" />
                  <span>Stock</span>
                </Box>
              }
            />
            <FormControlLabel 
              value="service" 
              control={<Radio />} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ServiceIcon fontSize="small" color="secondary" />
                  <span>Service</span>
                </Box>
              }
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            {defaultProductType === 'stock' 
              ? 'Stock items track inventory quantities and can be purchased/sold'
              : 'Service items do not track inventory (e.g., labor, delivery charges)'}
          </Typography>
        </FormControl>
      </Paper>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<ExcelIcon />} label="Excel/CSV (Any Format)" />
        <Tab icon={<ImageIcon />} label="Image/Menu" disabled={!aiStatus?.aiAvailable} />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Paper
            sx={{
              p: 4,
              border: '2px dashed #ccc',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => handleFileUpload(e, 'excel')}
            />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">Upload ANY Excel/CSV file</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Supports any column format - system will auto-detect and convert to product format
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Examples: Supplier price lists, competitor menus, inventory sheets, any product data
            </Typography>
          </Paper>

          {/* Supported formats info */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>✨ Auto-detects columns like:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['Product Name', 'Item', 'Description', 'Price', 'Cost', 'Category', 'Brand', 'SKU', 'Barcode', 'UOM', 'Unit'].map(col => (
                <Chip key={col} label={col} size="small" variant="outlined" />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download Standard Template
            </Button>
          </Box>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Paper
            sx={{
              p: 4,
              border: '2px dashed #ccc',
              borderRadius: 2,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'image')}
            />
            <ImageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">Upload product image, menu, or catalog</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              AI will extract product information from the image
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );

  // Target fields for our product format
  const TARGET_FIELDS = ['name', 'nativeName', 'mainCategory', 'posCategory', 'prodType', 'barcode', 'brand', 'uom', 'purchaseUom', 'cost', 'salesPrice', 'isRawMaterial'];

  const renderReviewStep = () => (
    <Box sx={{ p: 2 }}>
      {/* Column Mapping Info */}
      {parseInfo && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {parseInfo.usedAI ? '🤖 AI Auto-Mapped Columns' : '📋 Smart Column Detection'} 
          {parseInfo.confidence && ` (${Math.round(parseInfo.confidence * 100)}% confidence)`}
          {' - '}{parseInfo.parsedRows || parsedProducts.length} products extracted
          {parseInfo.sheetsProcessed && parseInfo.sheetsProcessed.length > 1 && 
            ` from ${parseInfo.sheetsProcessed.length} sheets`}
        </Alert>
      )}

      {/* Show sheets processed info */}
      {parseInfo?.sheetsProcessed && parseInfo.sheetsProcessed.length > 1 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ mr: 1 }}>📑 Sheets:</Typography>
          {parseInfo.sheetsProcessed.map((sheet, idx) => (
            <Chip 
              key={idx} 
              label={`${sheet.sheetName} (${sheet.productsCount})`} 
              size="small" 
              variant="outlined"
              color="secondary"
            />
          ))}
        </Box>
      )}

      {/* Show detected column mapping */}
      {columnMapping && Object.keys(columnMapping).length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>📊 Column Mapping (Your Excel → Our Format):</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(columnMapping).map(([key, value]) => {
              const originalHeader = typeof value === 'number' ? originalHeaders[value] : value;
              const targetField = typeof value === 'number' ? key : value;
              return (
                <Chip 
                  key={key} 
                  label={`${originalHeader || key} → ${targetField}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              );
            })}
          </Box>
        </Paper>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1">
          {selectedProducts.length} of {parsedProducts.length} products selected
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {aiStatus?.aiAvailable && (
            <Autocomplete
              size="small"
              options={categories}
              sx={{ width: 200 }}
              renderInput={(params) => <TextField {...params} label="Get AI Suggestions" />}
              onChange={(e, value) => value && handleGetSuggestions(value)}
            />
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedProducts.length === parsedProducts.length}
                  indeterminate={selectedProducts.length > 0 && selectedProducts.length < parsedProducts.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Product Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>POS Category</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>UOM</TableCell>
              <TableCell>Attribute</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedProducts.map((product, index) => (
              <TableRow key={index} selected={selectedProducts.includes(index)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedProducts.includes(index)}
                    onChange={() => handleSelectProduct(index)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    value={product.name || ''}
                    onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                    fullWidth
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={categories}
                    value={product.mainCategory || ''}
                    onChange={(e, v) => handleProductChange(index, 'mainCategory', v)}
                    onInputChange={(e, v) => handleProductChange(index, 'mainCategory', v)}
                    renderInput={(params) => <TextField {...params} variant="standard" />}
                    sx={{ minWidth: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={getPosCategoriesForProduct(product)}
                    value={product.posCategory || ''}
                    onChange={(e, v) => handleProductChange(index, 'posCategory', v)}
                    onInputChange={(e, v) => handleProductChange(index, 'posCategory', v)}
                    renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select or type new" />}
                    sx={{ minWidth: 120 }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={brands}
                    value={product.brand || ''}
                    onChange={(e, v) => handleProductChange(index, 'brand', v)}
                    onInputChange={(e, v) => handleProductChange(index, 'brand', v)}
                    renderInput={(params) => <TextField {...params} variant="standard" />}
                    sx={{ minWidth: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={uoms}
                    value={product.uom || 'pcs'}
                    onChange={(e, v) => handleProductChange(index, 'uom', v)}
                    onInputChange={(e, v) => handleProductChange(index, 'uom', v)}
                    renderInput={(params) => <TextField {...params} variant="standard" />}
                    sx={{ minWidth: 80 }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={attributes}
                    value={product.attribute || ''}
                    onChange={(e, v) => handleProductChange(index, 'attribute', v)}
                    onInputChange={(e, v) => handleProductChange(index, 'attribute', v)}
                    renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select or type new" />}
                    sx={{ minWidth: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={product.cost || 0}
                    onChange={(e) => handleProductChange(index, 'cost', parseFloat(e.target.value) || 0)}
                    variant="standard"
                    sx={{ width: 70 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={product.salesPrice || 0}
                    onChange={(e) => handleProductChange(index, 'salesPrice', parseFloat(e.target.value) || 0)}
                    variant="standard"
                    sx={{ width: 70 }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleDeleteProduct(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={() => setActiveStep(0)}>Back</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={selectedProducts.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          Import {selectedProducts.length} Products
        </Button>
      </Box>
    </Box>
  );

  const renderResultStep = () => (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>Import Complete!</Typography>
      
      {importResults && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 2, minWidth: 100 }}>
              <Typography variant="h4" color="success.main">{importResults.created}</Typography>
              <Typography variant="body2">Products Created</Typography>
            </Paper>
            <Paper sx={{ p: 2, minWidth: 100 }}>
              <Typography variant="h4" color="info.main">{importResults.updated}</Typography>
              <Typography variant="body2">Products Updated</Typography>
            </Paper>
            <Paper sx={{ p: 2, minWidth: 100 }}>
              <Typography variant="h4" color="error.main">{importResults.failed}</Typography>
              <Typography variant="body2">Failed</Typography>
            </Paper>
          </Box>

          {/* Show auto-created items */}
          {(importResults.categoriesCreated > 0 || importResults.posCategoriesCreated > 0 || importResults.attributesCreated > 0) && (
            <Alert severity="info" sx={{ textAlign: 'left', mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>✨ Auto-Created:</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {importResults.categoriesCreated > 0 && (
                  <Chip label={`${importResults.categoriesCreated} Categories`} size="small" color="primary" />
                )}
                {importResults.posCategoriesCreated > 0 && (
                  <Chip label={`${importResults.posCategoriesCreated} POS Categories`} size="small" color="secondary" />
                )}
                {importResults.attributesCreated > 0 && (
                  <Chip label={`${importResults.attributesCreated} Attributes`} size="small" color="success" />
                )}
              </Box>
            </Alert>
          )}

          {importResults.errors?.length > 0 && (
            <Alert severity="warning" sx={{ textAlign: 'left', mt: 2 }}>
              <Typography variant="subtitle2">Errors:</Typography>
              {importResults.errors.slice(0, 5).map((err, i) => (
                <Typography key={i} variant="body2">
                  Row {err.row}: {err.error}
                </Typography>
              ))}
              {importResults.errors.length > 5 && (
                <Typography variant="body2">...and {importResults.errors.length - 5} more</Typography>
              )}
            </Alert>
          )}
        </Box>
      )}

      <Button variant="contained" onClick={handleClose} sx={{ mt: 3 }}>
        Done
      </Button>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" />
          <Typography variant="h6">AI-Powered Product Import</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderUploadStep()}
        {activeStep === 1 && renderReviewStep()}
        {activeStep === 2 && renderResultStep()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductAIImport;
